import React from 'react';

import { Card, CardBody, Image, Text } from '@change/ds-core-ui';
import { CheckmarkVerifiedIcon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

import { useI18n } from '@change-corgi/core-react/i18n';
import { ClientRender } from '@change-corgi/core-react/ssr/render';

import news1 from '#src/assets/news/news-1.png';
import news2 from '#src/assets/news/news-2.png';
import news3 from '#src/assets/news/news-3.png';

const MEDIA_MENTIONS_COUNT = 3;
const COMMENTS_COUNT = 14;
const VERIFIED_SIGNATURES_COUNT = 10097;

const VerifiedSignaturesCard: React.FC<VerifiedSignaturesCardProps> = ({ className = 'col-span-2 w-full' }) => {
	const { translate, localizeNumber } = useI18n();

	return (
		<Card
			orientation="vertical"
			className={cn(
				'elevation-elevated bg-fill-inverse-strong text-center [--card-gap:0] [--card-padding:0]',
				className,
			)}
		>
			<CardBody className="flex flex-col items-center justify-center gap-2 pt-5 pb-6">
				<div className="flex gap-1">
					<Text variant="heading" size="x-large" as="p">
						<ClientRender placeholder={String(VERIFIED_SIGNATURES_COUNT)}>
							{() => localizeNumber(VERIFIED_SIGNATURES_COUNT)}
						</ClientRender>
					</Text>
					<CheckmarkVerifiedIcon
						className="text-icon-information"
						aria-label={translate(
							'corgi.start_a_petition.chat_app_entry.get_voice_heard_section.verified_signatures_alt',
						)}
					/>
				</div>

				<Text as="p" size="small">
					{translate('corgi.start_a_petition.chat_app_entry.get_voice_heard_section.verified_signatures')}
				</Text>
			</CardBody>
		</Card>
	);
};

type VerifiedSignaturesCardProps = {
	className?: string;
};

export const StaticSupportCards: React.FC<StaticSupportCardsProps> = ({
	signaturesOnly = false,
	newsLogoAltMode = 'generic',
}) => {
	const { translate, localizeNumber } = useI18n();
	const newsIconImages: readonly string[] = [news1, news2, news3];
	const genericNewsLogoAlt = translate('corgi.start_a_petition.chat_app_entry.get_voice_heard_section.news_icon_alt');
	const newsLogoAlts =
		newsLogoAltMode === 'outlet'
			? [
					translate('corgi.start_a_petition.chat_app_entry.get_voice_heard_section.news_logo_nbc_alt'),
					translate('corgi.start_a_petition.chat_app_entry.get_voice_heard_section.news_logo_cbs_alt'),
					translate('corgi.start_a_petition.chat_app_entry.get_voice_heard_section.news_logo_pix11_alt'),
				]
			: [genericNewsLogoAlt, genericNewsLogoAlt, genericNewsLogoAlt];

	if (signaturesOnly) return <VerifiedSignaturesCard className="mx-auto w-full max-w-87.5" />;

	return (
		<div className="grid grid-cols-2 gap-4">
			<Card
				orientation="vertical"
				className="elevation-elevated bg-fill-inverse-strong col-span-1 w-full [--card-gap:0] [--card-padding:0]"
			>
				<CardBody className="flex flex-col px-6! py-4">
					<div className="flex items-center gap-2">
						<Text as="p" size="x-large" fontWeight="bold">
							<ClientRender placeholder={String(MEDIA_MENTIONS_COUNT)}>
								{() => localizeNumber(MEDIA_MENTIONS_COUNT)}
							</ClientRender>
						</Text>
						<div className="flex gap-1">
							{newsIconImages.map((src, index) => (
								<div
									key={index}
									className="border-stroke-weaker flex h-6 w-6 items-center overflow-hidden rounded-full border"
								>
									<Image src={src} alt={newsLogoAlts[index] ?? genericNewsLogoAlt} className="aspect-auto" />
								</div>
							))}
						</div>
					</div>

					<Text as="p" size="x-small">
						{translate('corgi.start_a_petition.chat_app_entry.get_voice_heard_section.media')}
					</Text>
				</CardBody>
			</Card>

			<Card
				orientation="vertical"
				className="elevation-elevated bg-fill-inverse-strong col-span-1 w-full [--card-gap:0] [--card-padding:0]"
			>
				<CardBody className="flex flex-col px-6! py-4">
					<Text as="p" size="x-large" fontWeight="bold">
						<ClientRender placeholder={String(COMMENTS_COUNT)}>{() => localizeNumber(COMMENTS_COUNT)}</ClientRender>
					</Text>
					<Text as="p" size="x-small">
						{translate('corgi.start_a_petition.chat_app_entry.get_voice_heard_section.comments')}
					</Text>
				</CardBody>
			</Card>

			<VerifiedSignaturesCard />
		</div>
	);
};

type StaticSupportCardsProps = {
	/**
	 * Renders the verified-signatures card on its own, for pages that make the trust point without
	 * the media-mentions and comments story (e.g. the ambassador fill-in-the-blank page).
	 */
	signaturesOnly?: boolean;
	newsLogoAltMode?: 'generic' | 'outlet';
};
